// Client-side data access for Strava activities.
//
// All secret-bearing operations (OAuth token exchange, re-authorization with a
// refresh token, fetching activities with an access token) happen server-side in
// the Azure Functions API under /api. The browser never sees the client secret.

const STRAVA_CLIENT_ID = import.meta.env.VITE_STRAVA_CLIENT_ID || '90276';

/**
 * Fetch Logan's activities. Tries the API first (which can pull fresh data from
 * Strava using server-side secrets); falls back to the bundled static snapshot
 * so the site still renders if the API is unavailable (e.g. plain `vite dev`).
 */
export async function fetchLoganActivities() {
  try {
    const res = await fetch('/api/activities');
    if (res.ok) return await res.json();
  } catch {
    // ignore and fall back
  }
  const res = await fetch('/data/activities.json');
  if (!res.ok) throw new Error('Unable to load activities');
  return res.json();
}

/**
 * Fetch the signed-in visitor's own activities.
 * Pass `{ code }` straight after the Strava OAuth redirect, or `{ refToken }`
 * to reuse a previously stored refresh token. Returns
 * `{ refreshToken, activities }`.
 */
export async function fetchUserActivities({ code, refToken }) {
  const params = new URLSearchParams();
  if (code) params.set('code', code);
  if (refToken) params.set('refToken', refToken);
  const res = await fetch(`/api/user-activities?${params.toString()}`);
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    throw new Error(message || 'Unable to load your activities');
  }
  return res.json();
}

/** Build the Strava OAuth authorize URL that redirects back to `redirectUri`. */
export function buildAuthorizeUrl(redirectUri) {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
  const base = isMobile
    ? 'https://www.strava.com/oauth/mobile/authorize'
    : 'https://www.strava.com/oauth/authorize';
  const params = new URLSearchParams({
    client_id: STRAVA_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    approval_prompt: 'force',
    scope: 'activity:read_all',
  });
  return `${base}?${params.toString()}`;
}

const REF_TOKEN_KEY = 'lhr_strava_ref_token';

export function getStoredRefToken() {
  try {
    return localStorage.getItem(REF_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function storeRefToken(token) {
  try {
    if (token) localStorage.setItem(REF_TOKEN_KEY, token);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

export function clearRefToken() {
  try {
    localStorage.removeItem(REF_TOKEN_KEY);
  } catch {
    // ignore
  }
}
